import React, { useEffect, useMemo, useState } from "react";
import { Button, Tip } from "grommet";
import {
  Add,
  Calendar,
  Checkmark,
  Clock,
  FormNext,
  StatusWarning,
  Trash,
} from "grommet-icons";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { presentToastErrorContent, presentToastSuccess } from "@shared/Toast";
import { UserService } from "../../services";
import { avatarColor, initials } from "./avatar";
import { staffDepartmentLabel } from "./staffDepartments";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hours = Math.floor(index / 2);
  const minutes = index % 2 ? "30" : "00";
  const value = `${String(hours).padStart(2, "0")}:${minutes}`;
  return { value, label: moment(value, "HH:mm").format("h:mm A") };
});

const defaultDays = () =>
  DAYS.map((day) => ({
    day,
    enabled: true,
    allDay: true,
    start: "09:00",
    end: "17:00",
  }));

const scheduleFor = (user) => {
  const savedSchedule = user?.notificationSchedule;
  const hasSavedDays = Boolean(savedSchedule?.days?.length);
  return {
    alwaysAvailable: savedSchedule?.alwaysAvailable ?? !hasSavedDays,
    days: DAYS.map((day, index) => {
      const saved = user?.notificationSchedule?.days?.find(
        (entry) => entry.day === day,
      );
      return saved
        ? { ...saved, allDay: Boolean(saved.allDay) }
        : defaultDays()[index];
    }),
    exceptions: user?.notificationSchedule?.exceptions || [],
  };
};

const hasRestrictedSchedule = (user) => {
  const schedule = user?.notificationSchedule;
  return Boolean(
    schedule &&
    (schedule.alwaysAvailable === false || schedule.exceptions?.length),
  );
};

const isEmailOnly = (user) =>
  Boolean(
    user.notifEmail &&
    user.email &&
    !(user.notifText && user.phone) &&
    !(user.notifPush && user.mobilePushIds?.length),
  );

const hasScheduledCommunications = (user) =>
  Boolean(user.notifEmail || user.notifText || user.notifPush);

const isOnDuty = (user) => {
  const schedule = scheduleFor(user);
  const day = moment().format("dddd");
  const time = moment().format("HH:mm");
  const exception = schedule.exceptions.find(
    (item) => item.date === moment().format("YYYY-MM-DD"),
  );
  if (exception) {
    return Boolean(
      exception.enabled &&
      (exception.allDay ||
        (exception.start &&
          exception.end &&
          time >= exception.start &&
          time < exception.end)),
    );
  }
  if (schedule.alwaysAvailable) return true;
  const active = exception || schedule.days.find((item) => item.day === day);
  return Boolean(
    active?.enabled &&
    (active.allDay ||
      (active.start &&
        active.end &&
        time >= active.start &&
        time < active.end)),
  );
};

export default function StaffNotificationSchedule({ users, onRefresh }) {
  const { t, i18n } = useTranslation();
  const displayDay = (day, short = false) =>
    new Intl.DateTimeFormat(i18n.language, { weekday: short ? "short" : "long", timeZone: "UTC" }).format(
      new Date(Date.UTC(2024, 0, 1 + DAYS.indexOf(day))),
    );
  const [selectedId, setSelectedId] = useState();
  const [schedule, setSchedule] = useState(scheduleFor());
  const [saving, setSaving] = useState(false);

  const selected = useMemo(
    () =>
      users.find(
        (user) =>
          String(user.id) === String(selectedId) &&
          hasScheduledCommunications(user),
      ) || users.find(hasScheduledCommunications),
    [selectedId, users],
  );

  useEffect(() => {
    if (selected) {
      setSelectedId(selected.id);
      setSchedule(scheduleFor(selected));
    }
  }, [selected]);

  const updateDay = (day, fields) => {
    setSchedule((current) => ({
      ...current,
      days: current.days.map((entry) =>
        entry.day === day ? { ...entry, ...fields } : entry,
      ),
    }));
  };

  const copyMonday = () => {
    const monday = schedule.days[0];
    setSchedule((current) => ({
      ...current,
      days: current.days.map((entry) => ({
        ...entry,
        enabled: monday.enabled,
        allDay: monday.allDay,
        start: monday.start,
        end: monday.end,
      })),
    }));
  };

  const addException = () => {
    const nextDate = moment().add(1, "day").format("YYYY-MM-DD");
    setSchedule((current) => ({
      ...current,
      exceptions: [
        ...current.exceptions,
        { date: nextDate, enabled: false, start: "09:00", end: "17:00" },
      ],
    }));
  };

  const updateException = (index, fields) => {
    setSchedule((current) => ({
      ...current,
      exceptions: current.exceptions.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, ...fields } : entry,
      ),
    }));
  };

  const removeException = (index) => {
    setSchedule((current) => ({
      ...current,
      exceptions: current.exceptions.filter(
        (_, entryIndex) => entryIndex !== index,
      ),
    }));
  };

  const save = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      await UserService.update({
        id: selected.id,
        notificationSchedule: schedule,
      });
      presentToastSuccess(t("settingsUsers.scheduleSaved"));
      onRefresh();
    } catch (error) {
      presentToastErrorContent(error);
    } finally {
      setSaving(false);
    }
  };

  if (!selected) return null;

  return (
    <div className="staff-schedule">
      <div className="staff-schedule__notice">
        <Clock size="20px" />
        <div>
          <strong>{t("settingsUsers.scheduleNotice")}</strong>
          <span>{t("settingsUsers.scheduleNoticeDetail")}</span>
        </div>
      </div>

      <div className="staff-schedule__layout">
        <section className="staff-schedule__matrix panel-surface">
          <header>
            <div>
              <h3>{t("settingsUsers.weeklySchedule")}</h3>
              <p>{t("settingsUsers.weeklyScheduleDetail")}</p>
            </div>
            <Calendar size="22px" />
          </header>
          <div className="schedule-grid schedule-grid--header">
            <span>{t("settingsUsers.staffMember")}</span>
            {DAYS.map((day) => (
              <span key={day}>{displayDay(day, true)}</span>
            ))}
            <span aria-hidden="true" />
          </div>
          {users.map((user) => {
            const userSchedule = scheduleFor(user);
            const communicationsDisabled = !hasScheduledCommunications(user);
            return (
              <button
                type="button"
                className={`schedule-grid schedule-grid--row ${
                  String(user.id) === String(selected.id) ? "is-selected" : ""
                } ${communicationsDisabled ? "is-disabled" : ""}`}
                aria-pressed={String(user.id) === String(selected.id)}
                key={user.id}
                disabled={communicationsDisabled}
                onClick={() => setSelectedId(user.id)}
              >
                <span className="schedule-person">
                  <span
                    className="staff-avatar"
                    style={{
                      backgroundColor: avatarColor(user.username || user.email),
                    }}
                  >
                    {initials(user.username || user.email)}
                  </span>
                  <span>
                    <span className="schedule-person__name">
                      <strong>{user.username}</strong>
                      {hasRestrictedSchedule(user) && isEmailOnly(user) && (
                        <Tip
                          content={t("settingsUsers.emailOnlyScheduleWarning")}
                        >
                          <span className="schedule-email-warning">
                            <StatusWarning size="20px" />
                          </span>
                        </Tip>
                      )}
                    </span>
                    <small>{staffDepartmentLabel(user.department, t)}</small>
                    {communicationsDisabled && (
                      <small className="schedule-communications-disabled">
                        {t("settingsUsers.communicationsDisabled")}
                      </small>
                    )}
                  </span>
                </span>
                {userSchedule.days.map((day) => (
                  <span
                    className={`schedule-cell ${day.enabled ? "is-active" : ""}`}
                    key={day.day}
                  >
                    {communicationsDisabled ? (
                      "-"
                    ) : userSchedule.alwaysAvailable ||
                      (day.enabled && day.allDay) ? (
                      <b>{t("settingsUsers.allDay")}</b>
                    ) : day.enabled ? (
                      <>
                        <b>{day.start}</b>
                        <b>{day.end}</b>
                      </>
                    ) : (
                      "-"
                    )}
                  </span>
                ))}
                <span className="schedule-row-caret" aria-hidden="true">
                  <FormNext size="18px" />
                </span>
              </button>
            );
          })}
        </section>

        <aside className="staff-schedule__editor panel-surface">
          <div className="schedule-editor__person">
            <span
              className="staff-avatar staff-avatar--large"
              style={{
                backgroundColor: avatarColor(
                  selected.username || selected.email,
                ),
              }}
            >
              {initials(selected.username || selected.email)}
            </span>
            <div>
              <strong>{selected.username}</strong>
              <span>
                {staffDepartmentLabel(selected.department, t)} ·{" "}
                {isOnDuty(selected)
                  ? t("settingsUsers.onDutyNow")
                  : t("settingsUsers.offDuty")}
              </span>
            </div>
          </div>
          <label className="schedule-always-available">
            <input
              type="checkbox"
              checked={schedule.alwaysAvailable}
              onChange={(event) =>
                setSchedule((current) => ({
                  ...current,
                  alwaysAvailable: event.target.checked,
                }))
              }
            />
            <span>
              <strong>{t("settingsUsers.alwaysAvailable")}</strong>
              <small>{t("settingsUsers.alwaysAvailableDetail")}</small>
            </span>
          </label>
          {!schedule.alwaysAvailable && (
            <>
              <h3>{t("settingsUsers.weeklyHours")}</h3>
              <div className="schedule-editor__days">
                {schedule.days.map((day) => (
                  <div className="schedule-editor__day" key={day.day}>
                    <label>
                      <input
                        type="checkbox"
                        checked={day.enabled}
                        onChange={(event) =>
                          updateDay(day.day, { enabled: event.target.checked })
                        }
                      />
                      {displayDay(day.day)}
                    </label>
                    <label className="schedule-editor__all-day">
                      <input
                        type="checkbox"
                        checked={day.allDay}
                        disabled={!day.enabled}
                        onChange={(event) =>
                          updateDay(day.day, { allDay: event.target.checked })
                        }
                      />
                      {t("settingsUsers.allDay")}
                    </label>
                    <select
                      value={day.start}
                      disabled={!day.enabled || day.allDay}
                      onChange={(event) =>
                        updateDay(day.day, { start: event.target.value })
                      }
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option value={option.value} key={option.value}>
                          {i18n.language?.startsWith("fr") ? option.value : option.label}
                        </option>
                      ))}
                    </select>
                    <span>-</span>
                    <select
                      value={day.end}
                      disabled={!day.enabled || day.allDay}
                      onChange={(event) =>
                        updateDay(day.day, { end: event.target.value })
                      }
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option value={option.value} key={option.value}>
                          {i18n.language?.startsWith("fr") ? option.value : option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="schedule-copy"
                onClick={copyMonday}
              >
                <Checkmark size="15px" /> {t("settingsUsers.copyMonday")}
              </button>
            </>
          )}

          <div className="schedule-editor__exceptions">
            <div className="schedule-editor__section-title">
              <div>
                <h3>{t("settingsUsers.exceptions")}</h3>
                <p>{t("settingsUsers.exceptionsDetail")}</p>
              </div>
              <Button
                icon={<Add size="small" />}
                label={t("settings.add")}
                onClick={addException}
                size="small"
              />
            </div>
            {schedule.exceptions.map((exception, index) => (
              <div
                className="schedule-exception"
                key={`${exception.date}-${index}`}
              >
                <input
                  type="date"
                  value={exception.date}
                  onChange={(event) =>
                    updateException(index, { date: event.target.value })
                  }
                />
                <label>
                  <input
                    type="checkbox"
                    checked={exception.enabled}
                    onChange={(event) =>
                      updateException(index, { enabled: event.target.checked })
                    }
                  />
                  {t("settingsUsers.working")}
                </label>
                <Button
                  hoverIndicator="dark-1"
                  icon={<Trash size="small" />}
                  size="small"
                  primary
                  color="status-critical"
                  title={t("settingsUsers.removeException")}
                  onClick={() => removeException(index)}
                />
              </div>
            ))}
          </div>
          <Button
            primary
            label={t("settingsUsers.saveSchedule")}
            onClick={save}
            disabled={saving}
          />
        </aside>
      </div>
    </div>
  );
}
